import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import get from 'lodash.get';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Formik, Form, Field, ErrorMessage, FieldArray } from 'formik';

import { APP_LABELS, FIELD_LENGTH } from '@config/app-config';
import Help from '@modules/shared/components/reusable-components/help';
import TextError from '@modules/shared/components/reusable-components/text-error';
import SuccessToaster from '@modules/shared/components/reusable-components/success-toaster';
import ErrorPopup from '@modules/employee-data-collection/components/upload-file/error-popup';
import PartialSaveWarningPopup from '@modules/shared/components/reusable-components/partial-save-popup';
import LastUpdateName from '@modules/annual-reports/components/anuual-reports-overview/last-update-name';

import {
  resetReduxState,
  postOvernightCampDataWages,
  getOvernightCampDataRolesList,
  getOvernightCampDataWagesList
} from '@redux/overnight-camp-data-staff/action';
import { getCampReportsDashboardOverviewData } from '@redux/overnight-camp-data-overview/action';

import { AssociationAdminLoginDetailsSelector } from '@redux/login/selectors';
import { GetWagesSelector } from '@redux/overnight-camp-data-staff/selectors';
import { ApiParaSelector } from '@redux/overnight-camp-data-apiParameter/selectors';

import helpicon from '@static/images/caretdoubleright.svg';

const Wages = props => {
  const {
    status,
    setLoader,
    LoginUserData,
    handleResetRedux,
    getTotalWagesData,
    ApiParameterData,
    handleGetCampReportsDashboard,
    handleGetOvernightCampDataWages,
    handlePostOvernightCampDataWages,
    handleGetOvernightCampDataWagesRolesList
  } = props;

  const role = get(LoginUserData, 'user.role[0]');

  const [help, setHelp] = useState(false);
  const [instructions, setInstructions] = useState('');
  const [errorPopup, setErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successPopup, setSuccessPopup] = useState(false);
  const [formError, setFormError] = useState('');
  const [resStatus, setResStatus] = useState();

  const [rolesOptions, setRolesOptions] = useState([]);

  const initialValues = {
    roles: [
      { role: '', wage: '' },
      { role: '', wage: '' },
      { role: '', wage: '' }
    ]
  };

  const editValues = {
    roles: Array.isArray(getTotalWagesData?.data?.wages?.grossDailyWagesData)
      ? getTotalWagesData.data.wages.grossDailyWagesData.map(item => ({
          role: item.wageRoleLkpId != null ? String(item.wageRoleLkpId) : '',
          wage: item.grossDailyWage != null ? String(item.grossDailyWage) : ''
        }))
      : [
          { role: '', wage: '' },
          { role: '', wage: '' },
          { role: '', wage: '' }
        ]
  };

  const validationSchema = Yup.object().shape({
    roles: Yup.array().of(
      Yup.object().shape({
        role: Yup.string().nullable(),
        wage: Yup.string().nullable()
      })
    )
  });

  const handleGetCall = success => {
    setLoader(true);

    handleGetOvernightCampDataWagesRolesList()
      .then(res => {
        setLoader(false);
        let rolesArray = [];
        if (res && res.status === 200 && res.data) {
          if (Array.isArray(res.data.Roles)) {
            rolesArray = res.data.Roles;
          } else if (Array.isArray(res.data.roles)) {
            rolesArray = res.data.roles;
          } else if (res.data.data && Array.isArray(res.data.data.roles)) {
            rolesArray = res.data.data.roles;
          }
        }
        if (rolesArray.length > 0) {
          const roleList = rolesArray.map(role => ({
            id: role.WageRoleLkpId ?? role.id ?? role.RoleLkpId,
            value: role.WageRoleName ?? role.name ?? role.RoleName
          }));
          setRolesOptions(roleList);
        } else {
          setErrorMessage(APP_LABELS.COMMON_ERROR_MESSAGE);
          setErrorPopup(true);
        }
      })
      .catch(() => {
        setLoader(false);
        setErrorMessage(APP_LABELS.COMMON_ERROR_MESSAGE);
        setErrorPopup(true);
      });

    handleGetOvernightCampDataWages(
      ApiParameterData?.branchNumber,
      ApiParameterData?.reportBatchId,
      ApiParameterData?.year
    )
      .then(res => {
        if (res && res.status === 200) {
          if (res.data?.data?.wages?.grossDailyWagesData.length < 3) {
            setFormError(
              '* Please fill out all three mandatory roles and wages before saving.'
            );
          }
          setLoader(false);
          if (success) {
            setSuccessPopup(true);
          }
        } else {
          setErrorMessage(APP_LABELS.COMMON_ERROR_MESSAGE);
          setErrorPopup(true);
        }
      })
      .catch(error => {
        setLoader(false);
        setErrorMessage(APP_LABELS.COMMON_ERROR_MESSAGE);
        setErrorPopup(true);
      });
  };

  useEffect(() => {
    handleGetCall();
    setInstructions(
      JSON.parse(sessionStorage.getItem('allinstructions'))['wages']
        ?.Instruction
    );
    return () => {
      handleResetRedux('getYusaOvernightCampWages');
    };
  }, []);

  const handleChange = (e, setFieldValue, name) => {
    const result = e.target.value.replace(/\D+/g, '');
    setFieldValue(name, result);
  };

  const handleShowHelp = () => {
    setHelp(true);
  };
  const hideShowHelp = () => {
    setHelp(false);
  };

  const checkValidRoles = values => {
    const filledRoles = values.roles.filter(
      r =>
        r.role &&
        r.wage &&
        String(r.role).trim() !== '' &&
        String(r.wage).trim() !== ''
    );
    if (filledRoles.length < 3) {
      setFormError(
        '* Please fill out all three mandatory roles and wages before saving.'
      );
    }
  };

  const handleSubmit = values => {
    setLoader(true);
    setFormError('');

    checkValidRoles(values);

    const hasInvalidId = values.roles.some(role => !role.role);
    if (hasInvalidId) {
      setFormError('* Please select a valid role for each wage before saving.');
      setLoader(false);
      return;
    }

    const grossDailyWagesData =
      getTotalWagesData?.data?.wages?.grossDailyWagesData || [];

    const payloadBody = {
      CreatedByPersonId: LoginUserData?.user?.personBranchId,
      UpdatedByPersonId: LoginUserData?.user?.personBranchId,

      Year: ApiParameterData?.year,
      BranchNumber: ApiParameterData?.branchNumber,
      ReportBatchId: ApiParameterData?.reportBatchId,

      RecordStatus: getTotalWagesData?.data?.wages?.recordStatus || '',
      WageId: getTotalWagesData?.data?.wages?.wageId || 0,
      GrossDailyWageData: values.roles.map(role => {
        const roleOption = rolesOptions.find(
          opt => String(opt.id) === String(role.role)
        );
        const existing =
          grossDailyWagesData.find(
            item => String(item.wageRoleLkpId) === String(role.role)
          ) || {};
        return {
          WageRoleMappingId: existing.wageRoleMappingId || 0,
          WageRoleName: roleOption ? roleOption.value : '',
          GrossDailyWage:
            role.wage === 0 || role.wage ? Number(role.wage) : null,
          WageRoleLkpId: role.role ? parseInt(role.role, 10) : 0
        };
      })
    };

    setSuccessPopup(false);
    handlePostOvernightCampDataWages(payloadBody).then(res => {
      if (res.status === 201) {
        setResStatus(res?.data?.RecordStatus);
        handleGetCall(true);
        handleGetCampReportsDashboard(
          ApiParameterData?.branchNumber,
          ApiParameterData?.year
        );
        if (res?.data?.RecordStatus === 'Completed') {
          setFormError('');
        }
      } else if (res.statusCode === 409) {
        setLoader(false);
        setErrorMessage(res.errorMessage);
        setErrorPopup(true);
      } else {
        setLoader(false);
        setErrorPopup(true);
      }
    });
  };

  return (
    <>
      <div>
        <button
          type="button"
          className="ymca-help-link text-decoration-none ymca-up-data-help-link annual-report-help"
          onClick={handleShowHelp}
        >
          <span>HELP</span>

          <img src={helpicon} className="img-fluid" alt="caret-icon" />
        </button>
        <div className="ymca-program">
          <div className="col-xl-12 col-lg-12 col-md-12 col-sm-12 col-12 pb-3">
            <div className="ymca-instructionBoxes">
              <h4 className="ymca-instructHeadline">
                {APP_LABELS.INSTRUCTIONS}
              </h4>
              <p className="ymca-instructTitle">
                <div
                  dangerouslySetInnerHTML={{
                    __html: instructions
                  }}
                />
              </p>
            </div>
          </div>
          {getTotalWagesData?.data?.wages?.recordStatus === 'Partial' && (
            <PartialSaveWarningPopup />
          )}
          <div className="col-lg-12 col-md-12 col-sm-12 col-12">
            <div className="ymca-selectWrap">
              <div className="ymca-programActivities txt-mandatory">
                For each position at your Summer Overnight Camp, what is the
                position's Gross Daily Wage (before taxes and deductions)? Enter
                a dollar value only, without a dollar sign.
              </div>
            </div>
          </div>
          <Formik
            initialValues={editValues || initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ values, setFieldValue }) => (
              <Form autoComplete="off">
                <>
                  <div className="ymca-tableResponsive">
                    <table className="table ymca-table table-striped ymca-autoLength">
                      <thead>
                        <tr className="ymca-cuttingData">
                          <th>Roles</th>
                          <th>
                            Gross Daily Wage (before taxes and deductions) $
                          </th>
                          <th className="ymca-defaultSpace">&nbsp;</th>
                          <th className="ymca-defaultSpace">&nbsp;</th>
                        </tr>
                      </thead>
                      <tbody className="ymca-cuttingBody ymca-lengths">
                        <FieldArray name="roles">
                          {({ push, remove }) => (
                            <>
                              {values.roles.map((item, index) => {
                                // Filter out already selected roles except for the current row
                                const availableRoles = rolesOptions.filter(
                                  option =>
                                    !values.roles.some(
                                      (r, i) =>
                                        i !== index &&
                                        String(r.role) === String(option.id)
                                    )
                                );

                                return (
                                  <tr key={index}>
                                    <td>
                                      <div className="form-group">
                                        <div
                                          style={{
                                            position: 'relative',
                                            width: '100%'
                                          }}
                                        >
                                          <Field
                                            as="select"
                                            name={`roles[${index}].role`}
                                            className="form-select"
                                            value={item.role}
                                            onChange={e => {
                                              setFieldValue(
                                                `roles[${index}].role`,
                                                e.target.value
                                              );
                                            }}
                                          >
                                            <option value="">
                                              Select Role
                                            </option>
                                            {availableRoles &&
                                              availableRoles.map(option => (
                                                <option
                                                  key={option.id}
                                                  value={option.id}
                                                >
                                                  {option.value}
                                                </option>
                                              ))}
                                          </Field>
                                          <ErrorMessage
                                            name={`roles[${index}].role`}
                                            component={TextError}
                                          />
                                        </div>
                                      </div>
                                    </td>

                                    <td className="ymca-fieldLine">
                                      <Field
                                        name={`roles[${index}].wage`}
                                        placeholder="Enter wage"
                                        className="form-control ml-2"
                                        maxLength={FIELD_LENGTH.TEXTBOX_LENGTH}
                                        onChange={e =>
                                          handleChange(
                                            e,
                                            setFieldValue,
                                            `roles[${index}].wage`
                                          )
                                        }
                                      />
                                      <ErrorMessage
                                        name={`roles[${index}].wage`}
                                        component={TextError}
                                      />
                                    </td>
                                    <td className="ymca-fieldLine">
                                      {values?.roles?.length !== 1 && (
                                        <button
                                          type="button"
                                          className="close ymca-camp-close-btn ml-2"
                                          aria-label="Close"
                                          onClick={() => {
                                            remove(index);
                                            checkValidRoles(values);
                                          }}
                                          disabled={
                                            role ===
                                              APP_LABELS.YUSA_DATA_VIEWER ||
                                            status?.toUpperCase() ===
                                              APP_LABELS.STATUS_SUBMITTED
                                          }
                                        >
                                          <span aria-hidden="true">
                                            &times;
                                          </span>
                                        </button>
                                      )}
                                    </td>

                                    <td>&nbsp;</td>
                                  </tr>
                                );
                              })}

                              <button
                                type="button"
                                onClick={() => push({ role: '', wage: '' })}
                                disabled={
                                  values.roles.length >=
                                    (rolesOptions ? rolesOptions.length : 0) ||
                                  role === APP_LABELS.YUSA_DATA_VIEWER ||
                                  status?.toUpperCase() ===
                                    APP_LABELS.STATUS_SUBMITTED
                                }
                                className="btn btn-link camp-custom-add-btn mt-2"
                              >
                                <i className="fas fa-plus" /> Add New
                              </button>
                            </>
                          )}
                        </FieldArray>
                      </tbody>
                      {formError && <p className="errorNote">{formError}</p>}
                    </table>
                  </div>
                  <div className="ymca-submitHighlighter">
                    <LastUpdateName
                      Username={
                        getTotalWagesData?.data?.wages?.updatedByName ||
                        getTotalWagesData?.data?.wages?.createdByName
                      }
                      Time={
                        getTotalWagesData?.data?.wages?.updatedDatetime ||
                        getTotalWagesData?.data?.wages?.createdDateTime
                      }
                    />
                  </div>
                  {role !== APP_LABELS.YUSA_DATA_VIEWER && (
                    <button
                      type="submit"
                      className="btn ymca-savingBTN ymca-btnRightUntitled float-end mt-5"
                      disabled={
                        status?.toUpperCase() === APP_LABELS.STATUS_SUBMITTED
                      }
                    >
                      Save
                    </button>
                  )}
                </>
              </Form>
            )}
          </Formik>
        </div>
      </div>
      {successPopup && <SuccessToaster Status={resStatus} />}

      <ErrorPopup
        handleCloseErrorPopup={() => {
          setErrorPopup(false);
        }}
        errorMessage={errorMessage}
        showErrorPopup={errorPopup}
      />

      {help && <Help hidePopup={hideShowHelp} keyValue="wages" />}
    </>
  );
};
Wages.propTypes = {
  setLoader: PropTypes.func.isRequired,
  handleResetRedux: PropTypes.func.isRequired,
  handleGetCampReportsDashboard: PropTypes.func.isRequired,
  handleGetOvernightCampDataWages: PropTypes.func.isRequired,
  handlePostOvernightCampDataWages: PropTypes.func.isRequired,
  handleGetOvernightCampDataWagesRolesList: PropTypes.func.isRequired,

  status: PropTypes.objectOf(PropTypes.any).isRequired,
  LoginUserData: PropTypes.objectOf(PropTypes.any).isRequired,
  ApiParameterData: PropTypes.objectOf(PropTypes.any).isRequired,
  getTotalWagesData: PropTypes.objectOf(PropTypes.any).isRequired
};

const mapDispatchToProps = {
  handleResetRedux: resetReduxState,
  handlePostOvernightCampDataWages: postOvernightCampDataWages,
  handleGetOvernightCampDataWages: getOvernightCampDataWagesList,
  handleGetCampReportsDashboard: getCampReportsDashboardOverviewData,
  handleGetOvernightCampDataWagesRolesList: getOvernightCampDataRolesList
};

const mapStateToProps = state => ({
  getTotalWagesData: GetWagesSelector(state),
  ApiParameterData: ApiParaSelector(state),
  LoginUserData: AssociationAdminLoginDetailsSelector(state)
});

export default connect(mapStateToProps, mapDispatchToProps)(React.memo(Wages));
